import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard } from 'src/app/services/auth-guard/auth.guard';
import { HomeGuard } from 'src/app/services/home-guard/home.guard';
import { PoliciesRoutePaths } from 'src/app/modules/policies/policies-route-paths';
import { HomeComponent } from './components/home/home.component';
import { CountrySelectionComponent } from './components/country-selection/country-selection.component';
import { CountrySelectedGuard } from './services/country-selected-guard/country-selected.guard';
import { DeviceRegisteredGuard } from './services/device-registered-guard/device-registered.guard';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { NotAuthenticatedGuard } from './services/not-authenticated-guard/not-authenticated.guard';
import { AcceptPoliciesComponent } from './components/accept-policies/accept-policies.component';
import { AppOnlyCountryComponent } from './components/country-selection/app-only-country/app-only-country.component';
import { LocalMetadataGuard } from './services/metadata-guard/metadata.guard';
import { PoliciesGuard } from './services/policies-guard/policies.guard';
import { EnvironmentGuard } from './services/environment-guard/environment.guard';
import { ConsentComponent } from './modules/registration/consents/consents.component';
import { ApiMetadataGuard } from './services/api-metadata-guard/api-metadata.guard';
import { MaskRegistrationComponent } from './modules/registration/mask-registration/mask-registration.component';
import { AddFlowDeviceComponent } from './components/add-flow-device/add-flow-device.component';
import { ExperienceLauncherComponent } from './modules/registration/experience-launcher/experience-launcher.component';
import { HipaaConsentComponent } from './modules/registration/hipaa-consent/hipaa-consent.component';
import { HipaaBenefitsComponent } from './modules/registration/hipaa-consent/hipaa-benefits/hipaa-benefits.component';
import { LinkRouterComponent } from './components/link-router/link-router.component';

const routes: Routes = [
  {
    path: '', // Left for now in the event we need immediate global events on resolve
    children: [
      {
        path: '',
        component: HomeComponent,
        canActivate: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          HomeGuard,
          CountrySelectedGuard,
          EnvironmentGuard,
        ],
        data: { skipMaskMetadata: true },
      },
      {
        path: 'account-registration',
        loadChildren: () =>
          import('./modules/registration/registration.module').then((m) => m.RegistrationModule),
        canActivateChild: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          EnvironmentGuard,
        ],
        canLoad: [ApiMetadataGuard, LocalMetadataGuard, CountrySelectedGuard, EnvironmentGuard],
        data: { skipMaskMetadata: true },
      },
      {
        path: 'experience-launcher',
        component: ExperienceLauncherComponent,
        canActivateChild: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          EnvironmentGuard,
        ],
        canActivate: [ApiMetadataGuard, LocalMetadataGuard, CountrySelectedGuard, EnvironmentGuard],
        data: { skipMaskMetadata: true },
      },
      {
        path: 'linkRouter',
        component: LinkRouterComponent,
        data: { skipMaskMetadata: true },
      },
      {
        path: 'support',
        loadChildren: () => import('./modules/support/support.module').then((m) => m.SupportModule),
        canActivateChild: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          EnvironmentGuard,
        ],
        canLoad: [ApiMetadataGuard, LocalMetadataGuard, CountrySelectedGuard, EnvironmentGuard],
        data: { skipMaskMetadata: true },
      },
      {
        path: 'country-selection',
        component: CountrySelectionComponent,
        data: { layout: 'country-selection' },
        canActivate: [LocalMetadataGuard, NotAuthenticatedGuard],
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./modules/dashboard/dashboard.module').then((m) => m.DashboardModule),
        canActivateChild: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          AuthGuard,
          DeviceRegisteredGuard,
        ],
        canLoad: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          AuthGuard,
          DeviceRegisteredGuard,
        ],
      },
      {
        // Year-end Sleep Story ("myAir Wrapped"). Mocked for non-production builds.
        path: 'sleep-story',
        loadChildren: () =>
          import('./modules/sleep-story/sleep-story.module').then((m) => m.SleepStoryModule),
        data: { layout: 'immersive' },
      },
      {
        path: 'history',
        loadChildren: () => import('./modules/history/history.module').then((m) => m.HistoryModule),
        canActivateChild: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          AuthGuard,
          DeviceRegisteredGuard,
        ],
        canLoad: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          AuthGuard,
          DeviceRegisteredGuard,
        ],
        data: { skipMaskMetadata: true },
      },
      {
        path: 'library',
        loadChildren: () =>
          import('./modules/coaching-library/coaching-library.module').then(
            (m) => m.CoachingLibraryModule,
          ),
        canActivateChild: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          AuthGuard,
          DeviceRegisteredGuard,
        ],
        canLoad: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          AuthGuard,
          DeviceRegisteredGuard,
        ],
        data: { skipMaskMetadata: true },
      },
      {
        path: 'coaching-history',
        loadChildren: () =>
          import('./modules/coaching-history/coaching-history.module').then(
            (m) => m.CoachingHistoryModule,
          ),
        canActivateChild: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          AuthGuard,
          DeviceRegisteredGuard,
        ],
        canLoad: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          AuthGuard,
          DeviceRegisteredGuard,
        ],
        data: { skipMaskMetadata: true },
      },
      {
        path: 'my-account',
        loadChildren: () =>
          import('./modules/my-account/my-account.module').then((m) => m.MyAccountModule),
        canActivateChild: [ApiMetadataGuard, LocalMetadataGuard, CountrySelectedGuard, AuthGuard],
        canLoad: [ApiMetadataGuard, LocalMetadataGuard, CountrySelectedGuard, AuthGuard],
      },
      {
        path: 'register-device',
        component: AddFlowDeviceComponent,
        canActivate: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          DeviceRegisteredGuard,
          CountrySelectedGuard,
        ],
      },
      {
        path: 'mask-selection',
        component: MaskRegistrationComponent,
        canActivate: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          AuthGuard,
          DeviceRegisteredGuard,
        ],
      },
      {
        path: 'give-consent',
        component: ConsentComponent,
        canActivate: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          AuthGuard,
          DeviceRegisteredGuard,
        ],
      },
      {
        path: 'hipaa-benefits',
        component: HipaaBenefitsComponent,
        canActivate: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          AuthGuard,
          DeviceRegisteredGuard,
        ],
      },
      {
        path: 'hipaa-consent',
        component: HipaaConsentComponent,
        canActivate: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          CountrySelectedGuard,
          AuthGuard,
          DeviceRegisteredGuard,
        ],
      },
      {
        path: 'forgot-password',
        component: ForgotPasswordComponent,
        canActivate: [ApiMetadataGuard, LocalMetadataGuard, CountrySelectedGuard, EnvironmentGuard],
        data: { skipMaskMetadata: true },
      },
      {
        path: PoliciesRoutePaths.root,
        loadChildren: () =>
          import('./modules/policies/policies.module').then((m) => m.PoliciesModule),
        canActivateChild: [ApiMetadataGuard, LocalMetadataGuard, PoliciesGuard, EnvironmentGuard],
        canLoad: [LocalMetadataGuard, ApiMetadataGuard, PoliciesGuard, EnvironmentGuard],
        data: { skipMaskMetadata: true },
      },
      {
        path: 'accept-policies',
        data: { layout: 'accept-policies' },
        component: AcceptPoliciesComponent,
        canActivate: [ApiMetadataGuard, LocalMetadataGuard, AuthGuard],
      },
      {
        path: 'accept-policies/policies',
        loadChildren: () =>
          import('./modules/policies/policies.module').then((m) => m.PoliciesModule),
        data: { layout: 'accept-policies/policies', skipMaskMetadata: true },
        canActivateChild: [ApiMetadataGuard, LocalMetadataGuard, CountrySelectedGuard],
        canLoad: [ApiMetadataGuard, LocalMetadataGuard, CountrySelectedGuard],
      },
      {
        path: 'myair-asia',
        component: AppOnlyCountryComponent,
        data: { layout: 'only-app-country' },
        canActivate: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          NotAuthenticatedGuard,
          PoliciesGuard,
          EnvironmentGuard,
        ],
      },
      {
        path: 'myair-emea',
        component: AppOnlyCountryComponent,
        data: { layout: 'only-app-country' },
        canActivate: [
          ApiMetadataGuard,
          LocalMetadataGuard,
          NotAuthenticatedGuard,
          PoliciesGuard,
          EnvironmentGuard,
        ],
      },
      { path: '**', redirectTo: '/', pathMatch: 'full' },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      anchorScrolling: 'enabled',
      scrollPositionRestoration: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
